import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setInfo(data); setState("paid"); return; }
        if (data.status === "expired") { setState("error"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="grain min-h-screen" data-testid="payment-success-page">
      <Nav />
      <section className="pt-40 md:pt-52 pb-32 px-6 md:px-10">
        <div className="max-w-2xl mx-auto text-center glass shape-r p-10 md:p-14">
          {state === "polling" && (
            <div data-testid="payment-polling">
              <Loader2 className="mx-auto animate-spin text-primary-c mb-6" size={44} />
              <h1 className="font-serif text-3xl md:text-4xl">Confirming your reservation…</h1>
              <p className="mt-4 text-fg/60">One moment while we verify your payment.</p>
            </div>
          )}
          {state === "paid" && (
            <div data-testid="payment-paid">
              <CheckCircle2 className="mx-auto text-primary-c mb-6" size={52} />
              <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Your date is reserved.</h1>
              <p className="mt-5 text-fg/70 md:text-lg">
                Thank you{info?.metadata?.name ? `, ${info.metadata.name}` : ""}! Your ${info?.amount} deposit is confirmed
                {info?.metadata?.preferred_date ? ` for ${info.metadata.preferred_date}` : ""}. Casey's team will be in touch to finalize the details.
              </p>
              <Link to="/" className="inline-flex items-center gap-2 mt-8 shape-r bg-primary-c text-bg font-medium px-7 py-3.5 hover:opacity-90 transition-opacity">Back home <ArrowRight size={16} /></Link>
            </div>
          )}
          {(state === "timeout" || state === "error") && (
            <div data-testid="payment-issue">
              <XCircle className="mx-auto text-fg/50 mb-6" size={48} />
              <h1 className="font-serif text-3xl md:text-4xl">We couldn't confirm just yet.</h1>
              <p className="mt-4 text-fg/60">If you completed payment, a confirmation email will follow. Otherwise, please try again.</p>
              <Link to="/drumtalk" className="inline-block mt-8 shape-r border border-white/25 px-7 py-3.5 font-medium hover:bg-white/10 transition-colors">Back to DrumTalk</Link>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
